import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { subscribeToUnreadNotifications, type InAppNotification } from '../services/inAppNotifications';
import { useToast } from '../hooks/useToast';
import { quietToastRoute } from '../utils/quietToastRoute';
import { bookedHere, pageTells, quietUntil, skipsToast } from '../utils/oneToast';
import { logger } from '../utils/logger';
import { isProposalEvent, showProposalBanner } from '../services/proposalBanner';

interface NotificationsContextType {
  notifications: InAppNotification[];
  unreadCount: number;
}

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  unreadCount: 0,
});

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
  return useContext(NotificationsContext);
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  const { showInfo, showSuccess } = useToast();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const isFirstLoadRef = useRef(true);
  const knownIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!currentUser) {
      setNotifications([]); // eslint-disable-line react-hooks/set-state-in-effect
      isFirstLoadRef.current = true;
      knownIdsRef.current = new Set();
      return;
    }

    isFirstLoadRef.current = true;
    knownIdsRef.current = new Set();

    logger.info('Setting up notifications listener', { userId: currentUser.uid });

    const unsubscribe = subscribeToUnreadNotifications(
      currentUser.uid,
      (unreadNotifs) => {
        if (isFirstLoadRef.current) {
          // Seed known IDs on first load so we don't toast for existing notifications
          knownIdsRef.current = new Set(unreadNotifs.map((n) => n.id));
          isFirstLoadRef.current = false;
        } else {
          // Toast for genuinely new notifications.
          // Kept for the bell, not toasted, on onboarding, booking and the call (quietToastRoute).
          const quiet = typeof window !== 'undefined' && quietToastRoute(window.location.pathname);
          // Hidden, the push's notification from the service worker tells it: the page adds no toast or banner,
          // so it is told once (pageTells, 5 Nov). It stays on the bell either way.
          const hidden = typeof document !== 'undefined' && !pageTells(document.visibilityState);
          for (const n of unreadNotifs) {
            if (!knownIdsRef.current.has(n.id)) {
              knownIdsRef.current.add(n.id);
              if (quiet || hidden) continue;
              // The proposal's thread line (a system message): the sender has "Sent to Ana", the answerer the banner, so
              // no third telling as a message toast (the walk of 3 Nov). It stays on the bell.
              if ((n.data as { messageType?: string } | undefined)?.messageType === 'live_lesson_time_proposal') continue;
              // A time proposal (160; contracts 15): its banner with the push's words, not a toast (ProposalBanner).
              if (isProposalEvent(n.data as Record<string, unknown> | undefined)) {
                showProposalBanner({ id: n.id, title: n.title || 'New times suggested', message: n.message, link: n.link });
                continue;
              }
              // One toast per action: a booking the action already said (Booked, a weekly time) stays quiet;
              // the coach's, and every other, still shows (utils/oneToast).
              if (skipsToast(n, { nowMs: Date.now(), quietUntil: quietUntil(), bookedHere })) continue;
              if (n.type === 'like' || n.type === 'follow') {
                showSuccess(n.message);
              } else {
                showInfo(n.message);
              }
            }
          }
          // Remove IDs that are no longer unread (marked as read)
          const currentIds = new Set(unreadNotifs.map((n) => n.id));
          for (const id of knownIdsRef.current) {
            if (!currentIds.has(id)) knownIdsRef.current.delete(id);
          }
        }
        setNotifications(unreadNotifs);
      },
      (error) => {
        logger.error('Error in notifications listener', { userId: currentUser.uid }, error);
      }
    );

    return () => {
      logger.info('Cleaning up notifications listener', { userId: currentUser.uid });
      unsubscribe();
    };
  }, [currentUser?.uid]);

  return (
    <NotificationsContext.Provider value={{ notifications, unreadCount: notifications.length }}>
      {children}
    </NotificationsContext.Provider>
  );
}
